import { useEffect, useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useToastStore } from "../../../stores/toastStore";
import type { ManageCompliancePayload } from "../../types/logReview";

const MAX_BYTES = 1024 * 1024;
const ACCEPTED = ".jpg,.jpeg,.png,.pdf";
const JUSTIFICATION_MAX = 1000;

export interface ManageComplianceModalProps {
  open: boolean;
  selectedIds: string[];
  /** Disables the form + shows a busy Submit while the request is in flight. */
  submitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: ManageCompliancePayload) => void;
}

const formatBytes = (b: number) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

/**
 * Raises a justification against the selected non-compliant CR(s), with
 * optional supporting documents. Oversized files are rejected individually
 * so one bad pick doesn't discard the rest of the selection.
 */
export const ManageComplianceModal = ({
  open,
  selectedIds,
  submitting = false,
  onOpenChange,
  onSubmit,
}: ManageComplianceModalProps) => {
  const showError = useToastStore((s) => s.showError);
  const fileRef = useRef<HTMLInputElement>(null);
  const [justification, setJustification] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    if (open) {
      setJustification("");
      setFiles([]);
    }
  }, [open]);

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const next: File[] = [];
    for (const f of Array.from(fileList)) {
      if (f.size > MAX_BYTES) {
        showError("File too large", `${f.name} exceeds the 1 MB limit`);
        continue;
      }
      next.push(f);
    }
    if (next.length) setFiles((curr) => [...curr, ...next]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (index: number) =>
    setFiles((curr) => curr.filter((_, i) => i !== index));

  const submit = () => {
    if (!justification.trim() || submitting) return;
    onSubmit({
      rowIds: selectedIds,
      justification: justification.trim(),
      attachments: files,
    });
  };

  const submitDisabled = !justification.trim() || submitting;

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Manage Compliance"
      footer={
        <>
          <Button
            variant="secondary"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button disabled={submitDisabled} onClick={submit}>
            {submitting ? "Submitting…" : "Submit"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          No. of CR(s) selected:{" "}
          <span className="font-semibold text-foreground">{selectedIds.length}</span>
        </p>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label
              htmlFor="lr-justification"
              className="text-sm font-semibold text-foreground"
            >
              Justification <span className="text-destructive">*</span>
            </label>
            <span className="text-xs tabular-nums text-muted-foreground">
              {justification.length}/{JUSTIFICATION_MAX}
            </span>
          </div>
          <Textarea
            id="lr-justification"
            rows={6}
            placeholder="Enter your justification here"
            value={justification}
            disabled={submitting}
            onChange={(e) =>
              setJustification(e.target.value.slice(0, JUSTIFICATION_MAX))
            }
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-foreground">
            Attach Supporting Document(s)
          </span>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              disabled={submitting}
              onClick={() => fileRef.current?.click()}
            >
              <Paperclip className="size-4" aria-hidden />
              Choose Files
            </Button>
            <span className="text-sm text-muted-foreground">
              {files.length === 0
                ? "No file chosen"
                : `${files.length} ${files.length === 1 ? "file" : "files"}`}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED}
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Accepted: jpg, jpeg, png, pdf · max 1 MB per file
          </p>

          {files.length > 0 && (
            <ul className="mt-1 flex flex-col gap-1.5">
              {files.map((file, i) => (
                <li
                  key={`${file.name}-${i}`}
                  className="flex items-center justify-between rounded border border-border bg-card px-3 py-1.5 text-sm"
                >
                  <span className="truncate text-foreground">
                    {file.name}{" "}
                    <span className="text-muted-foreground">
                      ({formatBytes(file.size)})
                    </span>
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeFile(i)}
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
};
